import { ChevronDown, Clock } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { cn } from '@/lib/utils'
import { AnimatedCheckmark, HearthGlow } from '@/components/hearth'

interface PeaceStateHeroProps {
  /** Name of the family member being viewed (e.g., "Mom") */
  memberName?: string
  /** Whether viewing the whole family vs a specific member */
  isFamily?: boolean
  /** Override the default title/subtitle (page-specific variants) */
  title?: string
  subtitle?: string
  /** When the underlying data was last refreshed */
  lastUpdated?: string | Date
  /** Callback when "View details" is clicked */
  onViewDetails?: () => void
  /** Whether details are expanded */
  detailsExpanded?: boolean
  className?: string
}

/**
 * The "all is well" hero — Hearth's default state. A floating card with a
 * breathing checkmark over a soft sage bloom and a serif reassurance line.
 */
export function PeaceStateHero({
  memberName,
  isFamily = false,
  title,
  subtitle,
  lastUpdated,
  onViewDetails,
  detailsExpanded = false,
  className,
}: PeaceStateHeroProps) {
  const primaryText =
    title ??
    (isFamily
      ? 'Your family is doing well'
      : memberName
        ? `${memberName} is doing well`
        : 'All is well')

  const secondaryText =
    subtitle ?? "We're keeping watch. We'll let you know if anything changes."

  return (
    <div
      className={cn(
        'hearth-card-floating relative flex flex-col items-center justify-center overflow-hidden px-6 py-10 text-center',
        className
      )}
    >
      <HearthGlow />
      <div className="relative flex flex-col items-center">
        <AnimatedCheckmark size={80} />

        <h2 className="mt-4 font-serif text-2xl font-semibold text-success-800">
          {primaryText}
        </h2>

        <p className="mt-2 max-w-sm text-[15px] text-gray-500">{secondaryText}</p>

        {lastUpdated && (
          <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-success-500/10 px-3 py-1 text-xs font-medium text-success-600">
            <Clock className="h-3 w-3" />
            Updated {formatDistanceToNow(new Date(lastUpdated), { addSuffix: true })}
          </span>
        )}

        {onViewDetails && (
          <button
            onClick={onViewDetails}
            className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 transition-colors hover:text-gray-900"
          >
            <span>{detailsExpanded ? 'Hide details' : 'View details'}</span>
            <ChevronDown
              className={cn('h-4 w-4 transition-transform', detailsExpanded && 'rotate-180')}
            />
          </button>
        )}
      </div>
    </div>
  )
}
